import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import rg4js from "raygun4js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  type CaseFileLite,
  type CaseFilePage,
  CaseFileType,
  DocumentStatus,
  Source,
} from "@/briefpoint-client";
import { useCaseFileApi } from "@/hooks/useApi";
import useBatchErrorToasts from "@/hooks/useBatchErrorToasts";
import { ACCEPTED_FILE_TYPES } from "@/lib/constants";
import { getCaseFilesOptions } from "@/lib/query-options";
import {
  getSupportedAndUnsupportedFiles,
  normalizeError,
  uploadFilesToAzure,
} from "@/lib/utils";
import Button from "../Button";
import ExpandableText from "../ExpandableText";
import LeavingGuard from "../LeavingGuard";
import Loading from "../Loading";
import { toast } from "../ui/toast";
import FileList from "./FileList";
import styles from "./ProductionStep.module.scss";
import StepIndicator from "./StepIndicator";

interface Props {
  isComplete: boolean;
  setIsComplete: (isComplete: boolean) => void;
}

export default function ProductionStep(props: Props) {
  const caseFileApi = useCaseFileApi();
  const queryClient = useQueryClient();
  const batchErrorToasts = useBatchErrorToasts();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id: caseIdParam } = useParams<{ id: string }>();

  const [estimatedProgress, setEstimatedProgress] = useState(0);
  const [hasUploadedFiles, setHasUploadedFiles] = useState(false);
  // Track the number of files uploaded so we can calculate the progress
  const [fileCount, setFileCount] = useState(0);
  const [batchId, setBatchId] = useState<string | null>(null);

  const caseFilesQueryOptions = {
    ...getCaseFilesOptions({
      caseFileApi,
      caseId: caseIdParam ?? "",
      fileType: CaseFileType.Produceable,
      pageSize: 100,
      page: 1,
      checkForResponsiveFiles: true,
    }),
    enabled: Boolean(caseIdParam),
    select: (data: CaseFilePage) => {
      // show files that are not withheld
      const visible = (data.items ?? []).filter((file) => !file.isWithheld);
      return {
        files: visible.sort((a, b) => a.name.localeCompare(b.name)),
        pageCount: data.totalPages,
      };
    },
  };

  const caseFilesQuery = useQuery(caseFilesQueryOptions);

  // Upload files to Azure to create the upload batch
  const uploadFileMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const { data } = await uploadFilesToAzure({
        files,
        caseId: caseIdParam,
        type: CaseFileType.Produceable,
        caseFileApi,
        // Only let this get to 50%, since we have to wait for the batch process to get to 100%
        setEstimatedProgress: (progress) =>
          setEstimatedProgress(progress * 0.5),
      });
      return data;
    },
    onMutate: (files) => {
      setFileCount(files.length);
      // Set to incomplete state and show the progress bar
      props.setIsComplete(false);
    },
    onSuccess: async () => {
      // Trigger the polling query to check on the status of the azure upload.
      setHasUploadedFiles(true);
    },
    onError: (error) => {
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Unable to upload production documents.");
    },
  });

  const uploadBatchesQueryKey = useMemo(
    () => [
      "caseFileApi.caseFileGetCaseFileUploadBatches",
      "ProductionStep",
      { caseId: caseIdParam },
    ],
    [caseIdParam],
  );

  // When the azure upload is complete, poll the upload batch until it's empty
  const uploadBatchesQuery = useQuery({
    queryKey: uploadBatchesQueryKey,
    queryFn: () =>
      hasUploadedFiles
        ? caseFileApi.caseFileGetCaseFileUploadBatches({ caseId: caseIdParam })
        : // Hack to stop multiple requests when the component is re-mounted.
          Promise.resolve([]),
    enabled: hasUploadedFiles,
    retry: 1,
    retryDelay: 500,
    refetchInterval: (query) => {
      // When there are no documents in the batch, they have been uploaded. Stop polling.
      if (!query || query.state.data?.length === 0) {
        return false;
      }
      return 1500; // Poll every 1.5 seconds
    },
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data stale to ensure fresh polling,
  });

  // When all the files are uploaded and the batch is processed (i.e. empty)...
  useEffect(() => {
    if (!hasUploadedFiles || !uploadBatchesQuery.data) return;

    // If the batch is not empty, calculate the progress based on remaining files
    if ((uploadBatchesQuery.data?.length || 0) > 0) {
      if (fileCount === 0) return;

      // Save the batch id to so we can get the final batch to report errors
      setBatchId(uploadBatchesQuery.data[0].id || null);

      // Track upload progress
      const remainingFiles = uploadBatchesQuery.data[0]?.files?.length || 0;
      const processedFiles = fileCount - remainingFiles;
      // Progress starts at 50% from upload, then goes to 100% as batch processes
      const batchProgress = (processedFiles / fileCount) * 50; // 50% for batch processing
      const totalProgress = 50 + batchProgress; // 50% from upload + batch progress
      setEstimatedProgress(Math.min(totalProgress, 100));
    } else {
      // If the batch is empty: update UI, then fetch the completed batch to show any errors (e.g. unsupported files from zip)
      const completedBatchId = uploadBatchesQuery.data?.[0]?.id ?? batchId;
      const caseId = caseIdParam;

      // Reset fileCount
      setFileCount(0);

      // Clear the uploadBatchesQuery cache so this conditional works the next time we upload.
      queryClient.removeQueries({
        queryKey: uploadBatchesQueryKey,
      });

      // Optimistically update the case files query with the new files (exclude .zip so we don't flash the zip before refetch returns extracted contents)
      const newFiles = (uploadFileMutation.data?.files || []).filter(
        (file) => !file.name?.toLowerCase().endsWith(".zip"),
      );
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: [
              ...(oldData.items || []),
              ...newFiles.map((file, index) => ({
                id: `temp-${uploadFileMutation.data?.id}-${index}`, // Temporary ID
                userId: uploadFileMutation.data?.userId ?? "",
                name: file.name ?? "",
                source: Source.Uploaded,
                status: DocumentStatus.Uploaded,
                uploadedByClient: false,
                isWithheld: false,
                isPrivileged: false,
                type: CaseFileType.Produceable,
              })),
            ]
              // Dedupe files with the same name. The backend will do this for us, but we need to do it here for optimistic updates.
              .filter(
                (file, index, self) =>
                  index === self.findIndex((t) => t.name === file.name),
              )
              .sort((a, b) => a.name.localeCompare(b.name)),
          };
        },
      );

      // Stop polling the batch and reveal the new files.
      setHasUploadedFiles(false);

      // Set the production step to complete
      props.setIsComplete(true);

      // Invalidate and refetch the production files query to replace optimistic data
      queryClient.invalidateQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });

      // Fetch the completed batch and show error toasts (unsupported files in zip, etc.)
      if (completedBatchId && caseId) {
        caseFileApi
          .caseFileGetCaseFileUploadBatch({
            caseId,
            uploadBatchId: completedBatchId,
          })
          .then((batch) => {
            if (
              batch?.errorResults &&
              Object.keys(batch.errorResults).length > 0
            ) {
              batchErrorToasts(batch);
            }
          })
          .finally(() => setBatchId(null));
      } else {
        setBatchId(null);
      }
    }
  }, [
    uploadBatchesQuery.data,
    hasUploadedFiles,
    uploadBatchesQueryKey,
    queryClient,
    props.setIsComplete,
    caseFilesQueryOptions.queryKey,
    uploadFileMutation.data?.id,
    uploadFileMutation.data?.files,
    uploadFileMutation.data?.userId,
    fileCount,
    batchId,
    caseIdParam,
    caseFileApi,
    batchErrorToasts,
  ]);

  const setWithheldMutation = useMutation({
    mutationFn: async (file: CaseFileLite) => {
      return caseFileApi.caseFileSetWithheld({
        caseId: caseIdParam,
        fileId: file.id,
        body: true,
      });
    },
    onMutate: async (file) => {
      await queryClient.cancelQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });
      const previousData = queryClient.getQueryData(
        caseFilesQueryOptions.queryKey,
      );
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: oldData.items?.filter((item) => item.id !== file.id),
          };
        },
      );
      return { previousData };
    },
    onSuccess: (_, file) => {
      toast.success(`${file.name} was removed`);
    },
    onError: (error, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          caseFilesQueryOptions.queryKey,
          context.previousData,
        );
      }
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Unable to remove file.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: ["caseFileApi.caseFileGetWithheldList", caseIdParam],
      });
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: async (file: CaseFileLite) => {
      return caseFileApi.caseFileDelete({
        caseId: caseIdParam,
        fileId: file.id,
      });
    },
    onMutate: async (file) => {
      // Cancel any outgoing refetch so that we don't overwrite any other changes
      await queryClient.cancelQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });

      // Snapshot the previous value of the query data
      const previousData = queryClient.getQueryData(
        caseFilesQueryOptions.queryKey,
      );

      // Optimistically update case files query to remove the file
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: oldData.items?.filter((item) => item.id !== file.id),
          };
        },
      );

      return { previousData };
    },
    onSuccess: (_, file) => {
      toast.success(`${file.name} was removed`);
    },
    onError: (error, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          caseFilesQueryOptions.queryKey,
          context.previousData,
        );
      }
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Unable to delete complaint file.", {
        description: "Error:",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    const { supported, unsupported } = getSupportedAndUnsupportedFiles(
      selectedFiles,
      true,
    );

    // Clear the input immediately after processing
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (supported.length > 0) {
      uploadFileMutation.mutate(supported);
    }

    if (unsupported.length > 0) {
      const unsupportedNames = unsupported.map((f) => `• ${f.name}`).join("\n");
      toast.warning(
        <div className={styles.unsupportedToastDescription}>
          <span className={styles.unsupportedToastMessage}>
            Please convert the files below to PDF and reupload them.
          </span>
          <ExpandableText
            text={unsupportedNames}
            lines={6}
            fontSize={12}
            className={styles.unsupportedToastList}
          />
        </div>,
        {
          closeButton: true,
          duration: Number.POSITIVE_INFINITY,
        },
      );
    }
  }

  const uploadProductionText =
    uploadFileMutation.isPending || hasUploadedFiles
      ? "Uploading production documents..."
      : "Upload production documents";

  const isUploading = uploadFileMutation.isPending || hasUploadedFiles;

  useEffect(() => {
    if (!isUploading) {
      props.setIsComplete(!!caseFilesQuery.data?.files?.length);
    }
  }, [isUploading, props.setIsComplete, caseFilesQuery.data?.files?.length]);

  return (
    <>
      <LeavingGuard
        when={isUploading}
        message="Your upload will stop if you leave this page."
        title="Upload in progress!"
      />
      <div className="d-flex">
        <StepIndicator
          stepNumber={2}
          isSuccess={props.isComplete}
          isLoading={isUploading}
        />
        <div className={styles.container}>
          <div className={clsx(styles.box, props.isComplete && styles.success)}>
            <Loading
              isLoading={caseFilesQuery.isLoading}
              align="left"
              className={styles.loading}
            >
              <div
                className={`${styles.contents} d-flex justify-content-between align-items-center`}
              >
                <div
                  data-testid="production-step-title"
                  className={styles.title}
                >
                  {props.isComplete ? (
                    <div
                      data-testid="production-step-title-text"
                      className="d-flex align-items-center"
                    >
                      {/* number of files will append a plus sign if there are more than 100 */}
                      {caseFilesQuery.data?.files?.length &&
                      caseFilesQuery.data.files.length > 0
                        ? `${caseFilesQuery.data.files.length}${
                            (caseFilesQuery.data.pageCount || 0) > 1 ? "+" : ""
                          } documents uploaded`
                        : "documents uploaded"}
                      <Button
                        data-testid="upload-more-button"
                        size="sm"
                        variant="link"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Upload More
                      </Button>
                    </div>
                  ) : (
                    uploadProductionText
                  )}
                </div>
                <form>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="d-none"
                    onChange={handleFileChange}
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                  />
                  {!isUploading && !props.isComplete && (
                    <Button
                      data-testid="upload-production-button"
                      size="sm"
                      type="button"
                      className={styles.uploadButton}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload Production
                    </Button>
                  )}
                </form>
              </div>
            </Loading>
            {isUploading && (
              <div
                className={styles.loaderBar}
                style={{ width: `${estimatedProgress}%` }}
              />
            )}
            {caseFilesQuery.data?.files &&
              caseFilesQuery.data.files.length > 0 && (
                <FileList
                  files={caseFilesQuery.data.files}
                  canRemove={(file) =>
                    !file.uploadedByClient || !file.hasResponsiveFile
                  }
                  onRemove={(file) => {
                    if (file.uploadedByClient && !file.hasResponsiveFile) {
                      setWithheldMutation.mutate(file);
                    } else {
                      deleteFileMutation.mutate(file);
                    }
                  }}
                  disableRemove={isUploading}
                />
              )}
          </div>
        </div>
      </div>
    </>
  );
}
